import type { FilterSelection } from "@carbon6/c6-frontend-design";
import type { Label } from "@/api";
import { labelExplanation } from "@/components/label";
import { labelTokenDisplay, type FilterSearchCandidate } from "@/components/filter";

// 라벨 key 정렬 우선순위. 여기 있는 key 가 앞이고, 나머지는 알파벳 순이다. 서버는 하드웨어 축 key 의
// 라벨 등록을 거부한다(HARDWARE_AXIS_KEYS 422). 하지만 기존 데이터에 남아 있으면 이 순서를 따른다.
const labelKeyOrder: readonly string[] = ["embodiment", "end_effector"];

// URL, 칩, 소스가 함께 쓰는 라벨 조건 한 개. 값 표기는 `key:value`(`tokenValue`)다.
export type LabelToken = {
  key: string;
  value: string;
};

export function labelTokenValue(token: LabelToken): string {
  return token.key + ":" + token.value;
}

// `key:value` → 토큰. 라벨 key 는 식별자라 콜론이 없으므로 첫 콜론에서 자른다. 한쪽이라도 비면 형식 오류다.
export function parseLabelToken(value: string): LabelToken | null {
  const separatorIndex = value.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex === value.length - 1) {
    return null;
  }
  return { key: value.slice(0, separatorIndex), value: value.slice(separatorIndex + 1) };
}

/**
 * 검색 콤보박스 후보. 카탈로그의 모든 라벨을 `key:value` 조건으로 표시한다(rules.md §17 추천 규칙).
 * 데이터셋 수 내림차순이다. 수가 없는 후보는 뒤에 두고 key 우선순위, 값 이름 순으로 정렬한다.
 * 같은 `key:value` 는 한 번만 둔다.
 */
export function createLabelSearchCandidates(
  labels: readonly Label[],
): readonly FilterSearchCandidate[] {
  const seen = new Set<string>();
  const candidates: FilterSearchCandidate[] = [];
  for (const label of labels) {
    const value = labelTokenValue(label);
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    candidates.push({
      value,
      label: labelTokenDisplay(label.key, label.value),
      // 설명 순서: 서버 description → FE 가이드 → key(labelExplanation 규칙, 최종 폴백만 여기서 정한다).
      description: labelExplanation(label) ?? label.key,
      color: label.color,
      ...(label.dataset_count !== undefined ? { count: label.dataset_count } : {}),
    });
  }
  return candidates.sort(compareCandidates);
}

// 토큰 목록 → 소스가 받는 facet 선택(key → 값 목록). 같은 key 의 중복 값은 합친다.
export function labelTokensToFilterSelection(tokens: readonly LabelToken[]): FilterSelection {
  const selection: Record<string, string[]> = {};
  for (const token of tokens) {
    const values = selection[token.key] ?? [];
    if (!values.includes(token.value)) {
      selection[token.key] = [...values, token.value];
    }
  }
  return selection;
}

function compareCandidates(left: FilterSearchCandidate, right: FilterSearchCandidate): number {
  const leftCount = left.count ?? -1;
  const rightCount = right.count ?? -1;
  if (leftCount !== rightCount) {
    return rightCount - leftCount;
  }
  const leftToken = parseLabelToken(left.value);
  const rightToken = parseLabelToken(right.value);
  if (leftToken === null || rightToken === null) {
    return left.label.localeCompare(right.label);
  }
  const keyOrder = compareLabelKey(leftToken.key, rightToken.key);
  return keyOrder === 0 ? leftToken.value.localeCompare(rightToken.value) : keyOrder;
}

export function compareLabelKey(left: string, right: string): number {
  const leftIndex = labelKeyOrder.indexOf(left);
  const rightIndex = labelKeyOrder.indexOf(right);
  if (leftIndex >= 0 || rightIndex >= 0) {
    if (leftIndex < 0) {
      return 1;
    }
    if (rightIndex < 0) {
      return -1;
    }
    return leftIndex - rightIndex;
  }
  return left.localeCompare(right);
}
